const STORAGE_KEY = 'kerkplanner_sound_enabled';

export type SoundEffect = 'success' | 'error' | 'warning' | 'info' | 'whoosh' | 'crack';

const soundMap: Record<SoundEffect, string> = {
  success: '/Wind_Sound_Effect_5.mp3',
  error: '/Wood_Crack_Sound_4.mp3',
  warning: '/Wooden_Branch_Cracks_Sound.mp3',
  info: '/Slow_Whoosh_Sound_Effect.mp3',
  whoosh: '/Whoosh_Sound_Effect_2.mp3',
  crack: '/Tree_Chopping_Sound_Effect.mp3',
};

const cache = new Map<string, AudioBuffer>();
let audioCtx: AudioContext | null = null;
let userGestureOccurred = false;

function getAudioContext(): AudioContext | null {
  if (!audioCtx) {
    try {
      audioCtx = new AudioContext();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

// Warm up the AudioContext on the first user interaction so it's ready when
// a toast fires (browsers suspend AudioContext until a gesture has occurred).
function onFirstGesture() {
  userGestureOccurred = true;
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => { /* ignore */ });
  }
  document.removeEventListener('click', onFirstGesture);
  document.removeEventListener('keydown', onFirstGesture);
  document.removeEventListener('touchstart', onFirstGesture);
}

document.addEventListener('click', onFirstGesture);
document.addEventListener('keydown', onFirstGesture);
document.addEventListener('touchstart', onFirstGesture);

async function loadSound(url: string): Promise<AudioBuffer | null> {
  if (cache.has(url)) return cache.get(url)!;
  const ctx = getAudioContext();
  if (!ctx) return null;
  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const buffer = await ctx.decodeAudioData(arrayBuffer);
    cache.set(url, buffer);
    return buffer;
  } catch {
    return null;
  }
}

export function isSoundEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // ignore
  }
}

export async function playSound(effect: SoundEffect, volume = 0.35): Promise<void> {
  if (!isSoundEnabled()) return;
  if (!userGestureOccurred) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return;
    }
  }

  const url = soundMap[effect];
  const buffer = await loadSound(url);
  if (!buffer) return;

  const source = ctx.createBufferSource();
  const gainNode = ctx.createGain();
  gainNode.gain.value = volume;
  source.buffer = buffer;
  source.connect(gainNode);
  gainNode.connect(ctx.destination);
  source.start(0);
}
